import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Receipt, DollarSign, Edit, Trash2, X, CreditCard, FileText, Printer } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import PrintableInvoice from './PrintableInvoice';
import { useAuth } from '../context/AuthContext';

const PAYMENT_TYPES = ['cash', 'zelle', 'cashapp', 'chime', 'check', 'financing', 'card', 'other'];
const STATUS_OPTIONS = ['pending', 'paid', 'partial', 'refunded'];

export default function InvoiceManager() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [printInvoice, setPrintInvoice] = useState(null);
  const [form, setForm] = useState({
    stock_number: '', customer_name: '', customer_id: '',
    sale_date: '', deal_date: '', sale_price: '', dealer_fee: '',
    sales_tax: '', total: '', payment_type: 'cash',
    salesperson: '', remarks: '', status: 'pending'
  });

  const fetchData = useCallback(async () => {
    try {
      const [invRes, sumRes] = await Promise.all([
        api.get('/api/invoices'),
        api.get('/api/invoices/summary')
      ]);
      setInvoices(invRes.data);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const calcTotal = (sp, df, st) => {
    return (parseFloat(sp) || 0) + (parseFloat(df) || 0) + (parseFloat(st) || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      sale_price: parseFloat(form.sale_price) || 0,
      dealer_fee: parseFloat(form.dealer_fee) || 0,
      sales_tax: parseFloat(form.sales_tax) || 0,
      total: calcTotal(form.sale_price, form.dealer_fee, form.sales_tax)
    };
    try {
      if (editing) {
        await api.put(`/api/invoices/${editing.id}`, payload);
        toast.success('Invoice updated');
      } else {
        await api.post('/api/invoices', payload);
        toast.success('Invoice created');
      }
      resetForm();
      fetchData();
    } catch { toast.error('Failed to save invoice'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await api.delete(`/api/invoices/${id}`);
      toast.success('Invoice deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (inv) => {
    setEditing(inv);
    setForm({
      stock_number: inv.stock_number || '', customer_name: inv.customer_name || '',
      customer_id: inv.customer_id || '', sale_date: inv.sale_date || '',
      deal_date: inv.deal_date || '', sale_price: inv.sale_price?.toString() || '',
      dealer_fee: inv.dealer_fee?.toString() || '', sales_tax: inv.sales_tax?.toString() || '',
      total: inv.total?.toString() || '', payment_type: inv.payment_type || 'cash',
      salesperson: inv.salesperson || '', remarks: inv.remarks || '', status: inv.status || 'pending'
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false); setEditing(null);
    setForm({ stock_number: '', customer_name: '', customer_id: '', sale_date: '', deal_date: '', sale_price: '', dealer_fee: '', sales_tax: '', total: '', payment_type: 'cash', salesperson: '', remarks: '', status: 'pending' });
  };

  const filtered = invoices.filter(inv => {
    const matchSearch = !search || 
      inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      inv.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      inv.stock_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusColor = (s) => {
    const map = { paid: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', partial: 'bg-blue-100 text-blue-700', refunded: 'bg-red-100 text-red-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  if (loading) return <div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="invoice-manager">
      {/* Summary Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Sales', value: `$${(summary?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'from-green-500 to-emerald-500' },
          { label: 'Dealer Fees', value: `$${(summary?.total_fees || 0).toLocaleString()}`, icon: Receipt, color: 'from-blue-500 to-cyan-500' },
          { label: 'Sales Tax', value: `$${(summary?.total_tax || 0).toLocaleString()}`, icon: FileText, color: 'from-amber-500 to-orange-500' },
          { label: 'Invoices', value: summary?.count || 0, icon: CreditCard, color: 'from-purple-500 to-pink-500' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid={`invoice-stat-${i}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${s.color} flex items-center justify-center`}>
                <s.icon className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm text-gray-500">{s.label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search invoices..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 rounded-xl border-gray-200"
              data-testid="invoice-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 rounded-xl" data-testid="invoice-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl" data-testid="add-invoice-btn">
          <Plus className="w-4 h-4 mr-2" /> New Invoice
        </Button>
      </div>

      {/* Invoice Table */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center" data-testid="invoice-empty">
          <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No invoices yet</h3>
          <p className="text-gray-500 text-sm">Create your first invoice to start tracking sales.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoice-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Invoice #</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Customer</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Stock #</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Sale Price</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Fee</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Tax</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Total</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Payment</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-900">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-gray-700">{inv.customer_name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{inv.stock_number || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">${(inv.sale_price || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-600">${(inv.dealer_fee || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-600">${(inv.sales_tax || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">${(inv.total || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 capitalize text-gray-600">{inv.payment_type}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(inv.status)}`}>{inv.status}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setPrintInvoice(inv)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600" data-testid={`print-invoice-${inv.id}`} title="Print"><Printer className="w-3.5 h-3.5" /></button>
                        <button onClick={() => startEdit(inv)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" data-testid={`edit-invoice-${inv.id}`}><Edit className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(inv.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editing ? 'Edit Invoice' : 'New Invoice'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Stock #</Label>
                <Input value={form.stock_number} onChange={e => setForm({...form, stock_number: e.target.value})} placeholder="e.g. WAS0326012" className="rounded-xl" data-testid="invoice-form-stock" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Customer Name</Label>
                <Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} placeholder="Customer name" className="rounded-xl" data-testid="invoice-form-customer" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Sale Date</Label>
                <Input type="date" value={form.sale_date} onChange={e => setForm({...form, sale_date: e.target.value})} className="rounded-xl" data-testid="invoice-form-sale-date" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Deal Date</Label>
                <Input type="date" value={form.deal_date} onChange={e => setForm({...form, deal_date: e.target.value})} className="rounded-xl" data-testid="invoice-form-deal-date" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Sale Price *</Label>
                <Input type="number" step="0.01" value={form.sale_price} onChange={e => setForm({...form, sale_price: e.target.value})} placeholder="0.00" className="rounded-xl" required data-testid="invoice-form-price" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Dealer Fee</Label>
                <Input type="number" step="0.01" value={form.dealer_fee} onChange={e => setForm({...form, dealer_fee: e.target.value})} placeholder="0.00" className="rounded-xl" data-testid="invoice-form-fee" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Sales Tax</Label>
                <Input type="number" step="0.01" value={form.sales_tax} onChange={e => setForm({...form, sales_tax: e.target.value})} placeholder="0.00" className="rounded-xl" data-testid="invoice-form-tax" />
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <span className="text-sm text-gray-500">Total: </span>
              <span className="text-lg font-bold text-gray-900">${calcTotal(form.sale_price, form.dealer_fee, form.sales_tax).toLocaleString()}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Payment Type</Label>
                <Select value={form.payment_type} onValueChange={v => setForm({...form, payment_type: v})}>
                  <SelectTrigger className="rounded-xl" data-testid="invoice-form-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_TYPES.map(p => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="rounded-xl" data-testid="invoice-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-gray-700 text-sm font-medium">Salesperson</Label>
              <Input value={form.salesperson} onChange={e => setForm({...form, salesperson: e.target.value})} placeholder="Salesperson name" className="rounded-xl" data-testid="invoice-form-salesperson" />
            </div>
            <div>
              <Label className="text-gray-700 text-sm font-medium">Remarks</Label>
              <Input value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} placeholder="Additional notes..." className="rounded-xl" data-testid="invoice-form-remarks" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl" data-testid="invoice-form-submit">
                {editing ? 'Update Invoice' : 'Create Invoice'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Preview */}
      <PrintableInvoice 
        invoice={printInvoice}
        open={!!printInvoice}
        onClose={() => setPrintInvoice(null)}
        companyName={user?.company_name || user?.dealership_name || user?.name || 'Dealer'}
        companyEmail={user?.email}
        companyPhone={user?.phone}
      />
    </div>
  );
}
